import * as React from 'react';

import { translate } from '~l10n/client';
import { useAtomValue, useSetAtom } from 'jotai';
import { RESET } from 'jotai/utils';

import { Container } from '~shared/components/Container/Container';
import { xAgoFormatter } from '~shared/components/helpers';
import { IconDocument, IconFolder } from '~shared/components/icons/standard';
import { Search } from '~shared/components/Table/update/components/Search';
import { DialogQuizItemList } from '~teacher/components/workspaces/DialogQuizItemList/DialogQuizItemList';
import { useQuizzes } from '~teacher/hook';
import {
  WorkspaceItem,
  useWorkspaceItems,
} from '~teacher/hook/workspaces/useWorkspaceItems';
import { AddQuizButton } from '~teacher/launchv2/AddQuizButton';
import { proCheck } from '~teacher/util/proCheck';

import { QuizOrFolder } from '../../../api/quiz/getQuizzes/types';
import { WorkspaceFolder } from '../../../api/workspaces/folders/types';
import {
  launchDialogStepAtom,
  noBackButtonAtom,
  quizIdAtom,
  quizNameAtom,
  quizTypeAtom,
  workspaceIdAtom,
  workspaceNameAtom,
  workspaceQuizIdAtom,
} from '../atoms';

export type OnSelectQuizHandler = (quiz: {
  quizId: number;
  quizName: string;
}) => void;

interface SelectQuizProps {
  onSelectQuiz: OnSelectQuizHandler;
}

export function SelectQuiz({ onSelectQuiz }: SelectQuizProps) {
  const workspaceId = useAtomValue(workspaceIdAtom);
  const setQuizType = useSetAtom(quizTypeAtom);
  const workspaceName = useAtomValue(workspaceNameAtom);
  const noBackButton = useAtomValue(noBackButtonAtom);
  const setWorkspaceQuizId = useSetAtom(workspaceQuizIdAtom);
  const setWorkspaceId = useSetAtom(workspaceIdAtom);
  const setQuizId = useSetAtom(quizIdAtom);
  const setQuizName = useSetAtom(quizNameAtom);
  const setStep = useSetAtom(launchDialogStepAtom);
  const [searchQuery, setSearchQuery] = React.useState('');
  const { data: personal, isFetching: isFetchingPersonal } = useQuizzes({
    enabled: !workspaceId,
  });
  const { data: workspaceData, isFetching: isFetchingWorkspace } =
    useWorkspaceItems({
      workspaceId,
      enabled: Boolean(workspaceId),
    });

  const data: (QuizOrFolder | WorkspaceItem)[] = React.useMemo(() => {
    // we need to fetch data and process it differently for either personal or workspace quizzes
    if (!workspaceId) {
      return personal
        ? Object.keys(personal.itemById)
            .map((key) => personal.itemById[key])
            .filter((item) => !item.deleted && !item.locked)
        : [];
    } else {
      // shared workspace
      return workspaceData;
    }
  }, [workspaceId, personal, workspaceData]);
  const [parentId, setParentId] = React.useState<string | null | number>(null);
  const parentIdFilter = React.useCallback(
    (data) => {
      if (searchQuery) return data;
      return data.filter((item) => {
        if (!proCheck.hasPaidPlan()) {
          return item.type === 'quiz' && !item.locked;
        }
        if (!workspaceId) {
          return item.parentId === parentId;
        }
        if (item.type === 'quiz') {
          return item.parentFolderId === parentId;
        }
        return item.parentId === parentId;
      });
    },
    [parentId, searchQuery, workspaceId]
  );
  const handleSelection = (item) => {
    if (item.type === 'folder') {
      setSearchQuery('');
      setParentId(item.id);
    } else {
      if (!workspaceId) {
        setQuizType('Quiz');
        setWorkspaceQuizId(RESET);
        setWorkspaceId(RESET);

        setQuizId(item.id);
        setQuizName(item.name);
        setStep('increment');
        onSelectQuiz?.({ quizId: item.id, quizName: item.name });
      } else {
        setQuizType('Workspace');
        setQuizId(RESET);

        setWorkspaceQuizId(item.id);
        setWorkspaceId(workspaceId);
        setQuizName(item.name);
        setStep('increment');
        onSelectQuiz?.({ quizId: item.id, quizName: item.name });
      }
    }
  };
  const handleBackClick = React.useCallback(() => {
    if (parentId === null) {
      // return to workspace menu
      setStep('decrement');
      return;
    }
    const newId = (data as (QuizOrFolder | WorkspaceFolder)[]).find(
      (item) => item.id === parentId
    ).parentId;
    setSearchQuery('');
    setParentId(newId ? newId : null);
  }, [data, parentId, setParentId, setSearchQuery, setStep]);
  // initial sort
  const columns = React.useMemo(
    () => [
      {
        id: 'type',
        disableSortBy: true,
        accessor: (item) => item.type,
        customWidth: '60px',
        Cell: ({ value }) =>
          value === 'quiz' ? <IconDocument /> : <IconFolder />,
      },
      {
        Header: translate('Name'),
        id: 'name',
        accessor: (item) => item.name,
        sortBy: 'alphanumeric',
      },
      {
        Header: translate('Modified'),
        id: 'modified',
        customWidth: '160px',
        sortType: (a, b) => {
          const getTime = (row) => {
            if (!workspaceId) {
              return new Date(row.original.date).getTime();
            }
            if (row.original.type === 'quiz') {
              return new Date(row.original.publishedDate).getTime();
            }
            return new Date(row.original.createdDate).getTime();
          };
          const first = getTime(a);
          const second = getTime(b);
          if (first > second) return 1;
          if (second > first) return -1;
          return 0;
        },
        accessor: (item) => {
          if (!workspaceId) {
            if (item.type === 'quiz') {
              return xAgoFormatter({ date: item.date });
            }
            return xAgoFormatter({ date: item.date });
          } else {
            return item.type === 'quiz'
              ? xAgoFormatter({ date: item.publishedDate })
              : xAgoFormatter({ date: item.createdDate });
          }
        },
      },
    ],
    [workspaceId]
  );

  return (
    <>
      <Container className={'pt-6'} width={1}>
        <Search
          name={workspaceName}
          setQuery={setSearchQuery}
          variant={'modal'}
        />
        <DialogQuizItemList
          data={data}
          workspaceName={workspaceName}
          onBack={handleBackClick}
          parentId={parentId}
          onSelection={handleSelection}
          filters={[parentIdFilter]}
          searchQuery={{ id: 'name', value: searchQuery }}
          isLoading={isFetchingPersonal || isFetchingWorkspace}
          columns={columns}
          rootIsFinal={noBackButton}
        >
          <AddQuizButton />
        </DialogQuizItemList>
      </Container>
    </>
  );
}
